import { lerpColor } from '../../color';
import { sag } from '../../ink';
import type { Body, Kit, WorldDress, WorldFx } from '../../world';

const TAU = Math.PI * 2;
const LEDS = 16;
const RUNES = 4;
const WAVES = 3;
const CORD_SAG = 0.04;
const STRUTS = [-Math.PI / 2, Math.PI / 6, (5 * Math.PI) / 6];
const EQ = [1, 0.55, 0.8, 0.4, 0.95, 0.5, 0.7, 0.35];

function ring(k: Kit, body: Body, s: number): void {
  k.g.ellipse(body.x, body.y, body.radius * s * body.scaleX, body.radius * s * body.scaleY);
}

function glint(k: Kit, body: Body, color: number, alpha: number): void {
  const r = body.radius;
  k.g.ellipse(body.x - 0.36 * r * body.scaleX, body.y - 0.42 * r * body.scaleY, 0.14 * r * body.scaleX, 0.08 * r * body.scaleY).fill({ color, alpha });
}

function clamp01(v: number): number {
  return Math.min(1, Math.max(0, v));
}

export const dress: WorldDress = {
  incomingCircle(k, body, color, alpha) {
    const { g, pal } = k;
    const r = body.radius;
    ring(k, body, 0.62);
    g.fill({ color: lerpColor(color, pal.ink, 0.22), alpha: 0.18 * alpha });
    ring(k, body, 0.89);
    g.stroke({ width: Math.max(1, r * 0.16), color: lerpColor(color, pal.ink, 0.35), alpha: 0.28 * alpha });
    ring(k, body, 0.66);
    ring(k, body, 0.48);
    g.stroke({ width: Math.max(1, r * 0.03), color: lerpColor(color, pal.ink, 0.5), alpha: 0.28 * alpha });
    ring(k, body, 0.24);
    g.fill({ color: lerpColor(color, pal.rim, 0.45), alpha: 0.5 * alpha });
    ring(k, body, 0.24);
    g.stroke({ width: Math.max(1, r * 0.03), color: pal.ink, alpha: 0.3 * alpha });
    glint(k, body, pal.rim, 0.35 * alpha);
  },

  mineCircle(k, body, alpha, width, fill) {
    const { g, pal } = k;
    const r = body.radius;
    k.trace(body);
    g.fill({ color: lerpColor(fill, pal.heldShade, 0.35), alpha });
    g.ellipse(body.x - 0.07 * r * body.scaleX, body.y - 0.08 * r * body.scaleY, 0.8 * r * body.scaleX, 0.8 * r * body.scaleY);
    g.fill({ color: lerpColor(fill, pal.heldShade, 0.55), alpha: 0.55 * alpha });
    g.ellipse(body.x + 0.06 * r * body.scaleX, body.y + 0.07 * r * body.scaleY, 0.7 * r * body.scaleX, 0.7 * r * body.scaleY);
    g.fill({ color: lerpColor(fill, pal.heldLight, 0.2), alpha: 0.45 * alpha });
    ring(k, body, 0.9);
    g.stroke({ width: Math.max(1, r * 0.07), color: lerpColor(fill, pal.heldLight, 0.5), alpha: 0.8 * alpha });
    for (const a of STRUTS) {
      g.moveTo(body.x + Math.cos(a) * r * 0.88 * body.scaleX, body.y + Math.sin(a) * r * 0.88 * body.scaleY);
      g.lineTo(body.x, body.y);
    }
    g.stroke({ width: Math.max(1.2, r * 0.045), color: pal.ink, alpha: 0.7 * alpha, cap: 'round' });
    ring(k, body, 0.13);
    g.fill({ color: pal.ink, alpha: 0.85 * alpha });
    g.circle(body.x - 0.03 * r, body.y - 0.03 * r, 0.06 * r).fill({ color: pal.heldLight, alpha: 0.9 * alpha });
    glint(k, body, pal.heldLight, 0.55 * alpha);
    k.trace(body);
    g.stroke({ width, color: k.outline, alpha, join: 'round' });
  },

  target(k, body, alpha, _spread, reach, spin, edge) {
    const { g, pal } = k;
    k.trace(body);
    g.fill({ color: pal.spot, alpha: 0.1 * alpha });
    k.ringDots(body, reach, LEDS, spin, (x, y, i) => {
      const tone = i % 4 === 0 ? pal.held : pal.spot;
      g.circle(x, y, Math.max(3, k.unit * 0.018)).fill({ color: tone, alpha: 0.14 * alpha });
      g.circle(x, y, Math.max(1.5, k.unit * 0.008)).fill({ color: lerpColor(tone, pal.rim, 0.3), alpha: 0.9 * alpha });
    });
    for (let j = 0; j < RUNES; j++) {
      const a = spin + Math.PI / 4 + (j * Math.PI) / 2;
      for (let s = 1; s <= WAVES; s++) {
        const rad = reach + k.unit * (0.012 + s * 0.013);
        const half = 0.12 + s * 0.04;
        g.moveTo(body.x + Math.cos(a - half) * rad, body.y + Math.sin(a - half) * rad);
        g.arc(body.x, body.y, rad, a - half, a + half);
        g.stroke({ width: Math.max(1, k.unit * 0.004), color: pal.spot, alpha: (0.75 - s * 0.17) * alpha, cap: 'round' });
      }
    }
    k.trace(body);
    g.stroke({ width: edge, color: pal.rim, alpha: 0.85 * alpha, join: 'round' });
  },

  pip(k, x, y, radius, on, last, alpha, width) {
    const { g, pal } = k;
    const tone = last ? pal.spot : pal.held;
    if (on) g.circle(x, y, radius * 2).fill({ color: tone, alpha: 0.2 * alpha });
    g.circle(x, y, radius).fill({ color: on ? lerpColor(tone, pal.rim, 0.25) : lerpColor(pal.bg, pal.rim, 0.14), alpha });
    g.stroke({ width: width * 0.8, color: pal.rim, alpha: (on ? 0.9 : 0.45) * alpha });
  },

  path(k, points, count, alpha) {
    const { g, pal } = k;
    g.moveTo(points[0]!, points[1]!);
    for (let i = 2; i < count; i += 2) {
      const x0 = points[i - 2]!;
      const y0 = points[i - 1]!;
      const x1 = points[i]!;
      const y1 = points[i + 1]!;
      const mid = sag(x0, y0, x1, y1, CORD_SAG);
      g.quadraticCurveTo(2 * mid.x - (x0 + x1) / 2, 2 * mid.y - (y0 + y1) / 2, x1, y1);
    }
    g.stroke({ width: Math.max(1.3, k.unit * 0.0035), color: pal.path, alpha: alpha * 2.2, cap: 'round', join: 'round' });
    const wrap = Math.max(2.5, k.unit * 0.011);
    for (let i = 2; i < count; i += 2) {
      const x0 = points[i - 2]!;
      const y0 = points[i - 1]!;
      const x1 = points[i]!;
      const y1 = points[i + 1]!;
      const len = Math.hypot(x1 - x0, y1 - y0);
      if (len < wrap * 3) continue;
      const mid = sag(x0, y0, x1, y1, CORD_SAG);
      const ux = (x1 - x0) / len;
      const uy = (y1 - y0) / len;
      g.moveTo(mid.x - ux * wrap, mid.y - uy * wrap).lineTo(mid.x + ux * wrap, mid.y + uy * wrap);
    }
    g.stroke({ width: Math.max(3, k.unit * 0.011), color: lerpColor(pal.spot, pal.path, 0.4), alpha: Math.min(1, alpha * 3.2), cap: 'butt' });
  },
};

export const fx: WorldFx = {
  bit(g, x, y, size, i, color, alpha, rot) {
    if (i % 3 === 0) {
      const c = Math.cos(rot);
      const s = Math.sin(rot);
      const l = size * 1.3;
      const w = size * 0.45;
      g.poly([x - c * l + s * w, y - s * l - c * w, x + c * l + s * w, y + s * l - c * w, x + c * l - s * w, y + s * l + c * w, x - c * l - s * w, y - s * l + c * w]);
    } else {
      g.circle(x, y, size * 0.8);
      g.circle(x + Math.cos(rot) * size * 1.5, y + Math.sin(rot) * size * 1.5, size * 0.3);
      g.circle(x - Math.sin(rot) * size * 1.2, y + Math.cos(rot) * size * 1.2, size * 0.22);
    }
    g.fill({ color, alpha });
  },

  milestone(g, m, pal) {
    const inner = m.r * 0.62;
    const led = Math.max(2, m.unit * 0.011);
    const rise = clamp01(m.grow * 1.6);
    for (let i = 0; i < LEDS; i++) {
      const a = -Math.PI / 2 + (i / LEDS) * TAU;
      const outer = inner + m.r * (0.18 + 0.42 * EQ[i % EQ.length]!) * rise;
      const cos = Math.cos(a);
      const sin = Math.sin(a);
      const tone = i % 4 === 0 ? pal.held : pal.spot;
      g.moveTo(m.x + cos * inner, m.y + sin * inner).lineTo(m.x + cos * outer, m.y + sin * outer);
      g.stroke({ width: m.width * 1.3, color: tone, alpha: m.fade * 0.75, cap: 'butt' });
      const tip = outer + led * 1.8;
      g.circle(m.x + cos * tip, m.y + sin * tip, led).fill({ color: lerpColor(tone, pal.rim, 0.3), alpha: m.fade * 0.9 });
    }
  },

  trail(g, x, y, size, angle) {
    g.circle(x, y, size * 0.55);
    g.circle(x + Math.cos(angle + 1.9) * size * 0.9, y + Math.sin(angle + 1.9) * size * 0.9, size * 0.25);
  },
};
